import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Chinese mirror of the Thai NAATI vs court-registered translator guide. */
const TITLE = "NAATI 认证译员与泰国法院登记译员的区别 — 该选哪一种？";
const DESC =
  "何时必须使用 NAATI 认证翻译、何时应选择泰国法院或律师公会登记译员，以及如何避免重复付费与被退件。";
const PATH = "/blog/naati-vs-court-registered-translator";
const URL = `/zh${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "NAATI 是什么？",
    answer:
      "NAATI 是澳大利亚的笔译与口译认证机构。澳大利亚的移民、教育与政府机关通常要求译文由 NAATI 认证译员完成，并附上译员编号与签署声明。",
  },
  {
    question: "泰国法院或律师公会登记译员适用于哪些情形？",
    answer:
      "适用于在泰国境内使用，或须经泰国外交部领事司与目的地使领馆认证的文件。这类译员的资格为泰国机关所承认，是认证链条中的常规选择。",
  },
  {
    question: "两者可以互相替代吗？",
    answer:
      "不能一概而论。澳大利亚机关通常不接受未经 NAATI 认证的译文；泰国机关也不会因为译文经 NAATI 认证而免除本地认证程序。请依受理机关要求选择，必要时两种译文都需准备。",
  },
  {
    question: "如何避免重复付费？",
    answer:
      "在下单翻译前先书面确认三件事：受理机关名称、可接受的译员资格、以及是否需要额外的领事或使领馆认证。顺序确认清楚后再翻译，可避免同一份文件翻译两次。",
  },
  {
    question: "译文需要包含哪些内容？",
    answer:
      "必须完整对应原件，包括印章、钢印、批注、手写补充与页码。遗漏印章文字是最常见的退件原因之一。",
  },
  {
    question: "NAATI 译文还需要领事认证吗？",
    answer:
      "视目的地而定。若文件仅在澳大利亚境内使用，通常只需 NAATI 译文；若还须在第三国使用，则可能仍需泰国领事司与该国使领馆的认证。",
  },
  {
    question: "如何核验译员资格？",
    answer:
      "NAATI 译员可依其编号核验；泰国登记译员可依其登记记录核验。收到译文时应确认声明、签署与联系资料完整可查。",
  },
];

export const Route = createFileRoute("/zh/blog/naati-vs-court-registered-translator")({
  component: ZhNaatiVsCourtTranslator,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "zh" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("naati-vs-court-registered-translator", "zh") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("naati-vs-court-registered-translator") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "NAATI 认证翻译, 泰语 NAATI 译员, 泰国法院登记译员, 泰国文件翻译认证, 澳大利亚移民翻译要求",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "zh"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "首页", url: "/zh" },
              { name: "指南", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("naati-vs-court-registered-translator"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Translation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const CHOOSE: Array<{ case_: string; pick: string }> = [
  { case_: "澳大利亚签证、移民、学历评估", pick: "NAATI 认证译员" },
  { case_: "泰国法院、政府机关递交", pick: "法院／律师公会登记译员" },
  { case_: "泰国文件送第三国使领馆认证", pick: "登记译员 + 领事司认证" },
  { case_: "澳大利亚境内私人机构（银行、雇主）", pick: "多数接受 NAATI 译文" },
  { case_: "同时在澳大利亚与泰国使用", pick: "两套译文分别准备" },
];

const STEPS: string[] = [
  "取得受理机关的书面要求，确认可接受的译员资格",
  "确认可接受语言与是否需要额外认证",
  "取得完整原件或认证副本",
  "选择对应资格的译员并完成全文翻译（含印章文字）",
  "依需要办理领事司与使领馆认证",
  "递交前逐项核对姓名拼写、日期与页数",
];

const MISTAKES: string[] = [
  "先翻译后确认要求，导致资格不符须重译",
  "译文遗漏印章、钢印或批注文字",
  "姓名拼写与护照不一致",
  "以为 NAATI 译文可免除泰国本地认证",
  "使用普通复印件作为翻译底稿",
];

function ZhNaatiVsCourtTranslator() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/zh" className="text-muted-foreground hover:text-foreground">
          首页
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          指南
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="naati-vs-court-registered-translator" locale="zh" />
      <p className="answer-summary speakable-summary">
        选择译员的唯一依据是受理机关接受哪一种资格：澳大利亚机关通常要求 NAATI 认证译员，
        而须经泰国领事司与使领馆认证的文件则应使用法院或律师公会登记译员。先确认要求再翻译，可避免同一份文件付两次费用。
      </p>

      <h2>1. 按用途选择</h2>
      <ul>
        {CHOOSE.map((x) => (
          <li key={x.case_}>
            <strong>{x.case_}：</strong> {x.pick}
          </li>
        ))}
      </ul>

      <h2>2. 建议的办理顺序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 常见错误</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. 常见问题</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考来源与相关服务</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.naati.com.au/" target="_blank" rel="noreferrer">
          NAATI（澳大利亚翻译资格认证机构）
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          泰国外交部领事司
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/zh/naati" className="text-primary underline">
            NAATI 认证翻译服务
          </Link>
        </li>
        <li>
          <Link to="/zh/sworn" className="text-primary underline">
            宣誓／登记译员服务
          </Link>
        </li>
        <li>
          <Link to="/zh/lgz" className="text-primary underline">
            文件认证与领事认证服务
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容复核日期 {PUBLISHED}。本指南仅为规划用的一般信息，不代表任何机关的决定。办理前请向受理机关确认最新要求。
      </p>
    </article>
  );
}
